import React, { useState } from "react";
import { Eye, EyeOff, Mail, Lock, ShieldCheck, HelpCircle, Building2 } from "lucide-react";
import { ASSETS, BRAND } from "../config/brand";
import { authApi, formatApiError } from "../api/client";
import TaxiBackground from "../components/TaxiBackground";
import ForgotPasswordModal from "../components/ForgotPasswordModal";

export default function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForgot, setShowForgot] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Por favor ingresa correo y contraseña");
      return;
    }
    setLoading(true);
    try {
      const user = await authApi.login(email.trim(), password);
      onLogin(user);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="login-page">
      {/* LEFT — brand hero con fondo animado de taxis + logo protagónico */}
      <div className="hidden lg:flex flex-col justify-between w-[55%] text-white p-10 relative overflow-hidden">
        {/* Fondo animado corporativo temático de taxis */}
        <TaxiBackground />

        {/* Top chip — sistema en línea */}
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            <span className="text-xs font-semibold tracking-wider uppercase">Sistema en línea · v0.8</span>
          </div>
        </div>

        {/* Centro — LOGO PROTAGÓNICO (contiene wordmark + tagline dentro) */}
        <div className="relative z-10 flex flex-col items-center text-center">
          <div className="relative logo-float">
            {/* Halo verde detrás del logo (respeta la marca) */}
            <div className="absolute inset-0 rounded-full bg-[#C6E90C]/25 blur-3xl scale-110"></div>
            <img
              src={ASSETS.logo}
              alt={BRAND.name}
              className="relative w-[420px] xl:w-[500px] h-auto object-contain drop-shadow-2xl"
              data-testid="login-brand-logo"
            />
          </div>
          <p className="mt-8 max-w-md text-white/80 text-base leading-relaxed">
            Plataforma corporativa de gestión de flota, socios y operaciones — todo desde un único centro de control.
          </p>
        </div>

        {/* Bottom — copyright limpio */}
        <div className="relative z-10 text-xs text-white/50 flex items-center justify-between">
          <div>© 2026 {BRAND.legal}</div>
          <div className="font-semibold text-white/80">Creado por Analytica</div>
        </div>
      </div>

      {/* RIGHT — login form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-[#F8F9FC] relative">
        <div className="w-full max-w-md relative z-10">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <img src={ASSETS.logo} alt={BRAND.name} className="h-32 w-auto object-contain" />
          </div>

          <div className="text-xs font-semibold tracking-widest uppercase text-[#3949AB] mb-2">Acceso corporativo</div>
          <h2 className="font-display font-bold text-3xl text-neutral-900 mb-1">Iniciar sesión</h2>
          <p className="text-sm text-neutral-500 mb-8">
            Ingresa con tu correo o teléfono corporativo para acceder al panel de control.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            {/* Email */}
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">Correo o teléfono</label>
              <div className="flex items-center gap-2.5 bg-white border border-neutral-300 rounded px-3 py-2.5 focus-within:border-[#3949AB] focus-within:ring-2 focus-within:ring-[#EEF0FC] transition-colors">
                <Mail className="w-4 h-4 text-neutral-400" strokeWidth={2}/>
                <input
                  data-testid="login-email"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="hugo@turismomdd.pe"
                  className="bg-transparent outline-none flex-1 text-sm"
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Contraseña</label>
                <button type="button" onClick={() => setShowForgot(true)} className="text-[11px] font-semibold text-[#3949AB] hover:underline" data-testid="forgot-password-btn">
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
              <div className="flex items-center gap-2.5 bg-white border border-neutral-300 rounded px-3 py-2.5 focus-within:border-[#3949AB] focus-within:ring-2 focus-within:ring-[#EEF0FC] transition-colors">
                <Lock className="w-4 h-4 text-neutral-400" strokeWidth={2}/>
                <input
                  data-testid="login-password"
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-transparent outline-none flex-1 text-sm"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((s) => !s)}
                  className="text-neutral-400 hover:text-neutral-600"
                  data-testid="login-toggle-pass"
                >
                  {showPass ? <EyeOff className="w-4 h-4" strokeWidth={2}/> : <Eye className="w-4 h-4" strokeWidth={2}/>}
                </button>
              </div>
            </div>

            {/* Remember */}
            <label className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="rounded border-neutral-300 text-[#3949AB]"
              />
              Mantener sesión iniciada
            </label>

            {error && (
              <div className="chip chip-danger" data-testid="login-error">{error}</div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full btn-primary !py-3 !text-sm justify-center"
            >
              {loading ? "Ingresando..." : (
                <>
                  Iniciar sesión
                  <ShieldCheck className="w-4 h-4" strokeWidth={2.5}/>
                </>
              )}
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-neutral-200"></div>
              <span className="text-[10px] font-semibold tracking-widest uppercase text-neutral-400">O continúa con</span>
              <div className="flex-1 h-px bg-neutral-200"></div>
            </div>

            {/* SSO */}
            <button
              type="button"
              className="w-full btn-secondary !py-2.5 justify-center"
              data-testid="login-sso"
            >
              <Building2 className="w-4 h-4" strokeWidth={2}/>
              SSO corporativo · TURISMO MADRE DE DIOS
            </button>
          </form>

          {/* Help */}
          <div className="mt-8 flex items-center justify-between text-xs text-neutral-500">
            <div className="flex items-center gap-1.5">
              <HelpCircle className="w-3.5 h-3.5" strokeWidth={2}/>
              <span>¿Problemas para acceder?</span>
              <a href="#" className="text-[#3949AB] font-semibold hover:underline">Contactar soporte</a>
            </div>
            <a href="#" className="text-[#3949AB] font-semibold hover:underline flex items-center gap-1">
              <HelpCircle className="w-3.5 h-3.5" strokeWidth={2}/> Ayuda
            </a>
          </div>
        </div>
      </div>
      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
    </div>
  );
}
